import type { JSX, ReactNode } from 'react';
import { STATUS_TONES, cx } from './styles';
import type { StatusLevel } from './styles';

export type BadgeVariant = 'soft' | 'outline';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  children: ReactNode;
  /** 语义等级。组件不判断"什么算错误"，只做等级 → 令牌的映射 */
  level?: StatusLevel;
  /** soft = 柔和底（默认）；outline = 只描边，用于密集列表里避免一片色块 */
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** 前置指示点：等级的第二条通道。色觉障碍与户外强光下颜色不可靠 */
  dot?: boolean;
  className?: string;
}

const SIZES: Record<BadgeSize, string> = {
  sm: 'gap-1 px-1.5 py-0 text-[10px] leading-4',
  md: 'gap-1 px-2 py-0.5 text-xs',
};

/**
 * 状态徽标（**只读**，不可点击）。要能点、能删、能当筛选条件请用 `Tag`。
 *
 * "颜色"与"语义"是分开的：
 *   调用方写 `<Badge level="critical">失控</Badge>`，而不是
 *   `<Badge className="bg-red-500">`。等级 → 令牌的映射集中在 styles.ts，
 *   所以换主题、加高对比度、甚至将来改"红色在我们这里的含义"都只改一处。
 *
 * 顺带的收益：徽标里**必须**有文字（`children` 是必填）——
 * 只有颜色没有字的徽标，等于把等级编码成"用户必须记住的一张色卡"。
 */
export function Badge({
  children,
  level = 'neutral',
  variant = 'soft',
  size = 'md',
  dot = false,
  className,
}: BadgeProps): JSX.Element {
  const tone = STATUS_TONES[level];

  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full border font-medium whitespace-nowrap',
        SIZES[size],
        tone.text,
        variant === 'outline' ? cx('bg-transparent', tone.border) : cx(tone.surface, tone.border),
        className,
      )}
    >
      {dot ? <span aria-hidden="true" className={cx('size-1.5 rounded-full', tone.fill)} /> : null}
      {children}
    </span>
  );
}
